import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Input,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import InfoView from 'components/common/InfoView';

import {
  addTransactionCharge,
  deleteTransactionCharge,
  getTransactionCharges,
  updateTransactionCharge,
} from '../../redux/actions/TransactionCharges';

import { TransactionChargesColumns } from '../../components/common/Columns';
import TransactionChargeForm from './TransactionChargeForm';
import generateQuery, { handleSearch as search } from '../../util/table';
import Can from '../../util/can';
import { TRANSACTION_CHARGES } from '../../constants/resources';
import useForm from '../../hooks/useForm';

const TransactionCharges = () => {
  const dispatch = useDispatch();

  const {
    data: transactionChargesData,
    loading,
    pageSize,
    currentPage,
    totalRecords,
  } = useSelector(({ transactionCharges }) => transactionCharges);

  const {
    data: processorsData,
    loading: processorsLoading,
  } = useSelector(({ processors }) => processors.allProcessors);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [selectedTransactionType, setSelectedTransactionType] = useState(null);
  const [selectedMinimumAmount, setSelectedMinimumAmount] = useState(0);
  const [selectedMaximumAmount, setSelectedMaximumAmount] = useState(0);
  const [selectedTrafficTypes, setSelectedTrafficTypes] = useState(null);

  const [searchParams, setSearchParams] = useState('');

  const {
    form,
    onFormOk,
    formVisibility,
    handleShowModal,
    handleCancel,
    handleEditRecord,
    editableRecord,
  } = useForm();

  useEffect(() => {
    const pagination = `page=${currentPage}&limit=${pageSize}`;
    dispatch(getTransactionCharges(pagination));
  }, [dispatch]);

  const editable = Object.keys(editableRecord).length !== 0;

  const handleDeleteTransactionCharge = (id) => {
    dispatch(deleteTransactionCharge(id));
  };

  const handleEditTransactionCharge = (record) => {
    setSelectedMinimumAmount(record?.minimum_amount || 0);
    setSelectedMaximumAmount(record?.maximum_amount || 0);
    setSelectedPaymentMethod(
      record.payment_method,
    );
    setSelectedCurrency(
      record.currency,
    );
    setSelectedProvider(
      record.provider,
    );
    setSelectedProcessor(
      record.processor,
    );
    setSelectedTransactionType(
      record.transaction_type,
    );
    setSelectedTrafficTypes(record.traffic_type);
  };

  const handleOnChange = (pagination, filters, sorter) => {
    dispatch(getTransactionCharges(generateQuery(pagination, filters, sorter, searchParams)));
  };

  const handleSearch = (searchValue) => {
    const query = search(
      searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1'),
      dispatch, getTransactionCharges, TransactionChargesColumns,
    );
    setSearchParams(query);
  };

  const props = {
    loading: ((processorsLoading) || loading),
    pagination: {
      current: currentPage,
      pageSize,
      total: totalRecords,
      showSizeChanger: false,
    },
    onChange: handleOnChange,
    rowKey: '_id',
    columns: TransactionChargesColumns(
      handleDeleteTransactionCharge,
      handleEditTransactionCharge,
      handleEditRecord,
      processorsData,
    ),
    dataSource: transactionChargesData.map((item) => ({
      ...item,
      processor: {
        _id: item.processor._id,
        name: item.processor.name || processorsData.find(
          (element) => element._id === item.processor,
        )?.name,
        traffic_type: item.processor.traffic_type || processorsData.find(
          (element) => element._id === item.processor,
        )?.traffic_type,
      }
    })),

    scroll: { x: 1300 },
  };

  const handleCancelForm = () => {
    setSelectedPaymentMethod(null);
    setSelectedCurrency(null);
    setSelectedTransactionType(null);
    setSelectedProcessor(null);
    setSelectedProvider(null);
  };

  const handleOk = (value) => {
    const transactionChargeData = {
      ...value,
      payment_method: selectedPaymentMethod,
      currency: selectedCurrency,
      transaction_type: selectedTransactionType,
      processor: selectedProcessor._id,
      provider: selectedProvider,
      src_currency: selectedCurrency,
      src_charge: 0,
      p2p_fixed_margin: 0,
      p2p_percentage_margin: 0,
      minimum_amount: Number(selectedMinimumAmount),
      maximum_amount: Number(selectedMaximumAmount),
      traffic_types: selectedTrafficTypes,
    };
    if (editable) {
      dispatch(updateTransactionCharge(editableRecord._id, transactionChargeData));
    } else {
      dispatch(addTransactionCharge(transactionChargeData));
    }
  };

  const handleChange = (value, key) => {
    if (key === 'currency') {
      setSelectedCurrency(value);
    } else if (key === 'payment_method') {
      setSelectedPaymentMethod(value);
    } else if (key === 'processor') {
      setSelectedProcessor(value);
    } else if (key === 'provider') {
      setSelectedProvider(value);
    } else if (key === 'transaction_type') {
      setSelectedTransactionType(value);
    } else if (key === 'minimum_amount') {
      setSelectedMinimumAmount(value);
    } else if (key === 'maximum_amount') {
      setSelectedMaximumAmount(value);
    } else if (key === 'traffic_types') {
      setSelectedTrafficTypes(value);
    }
  };

  return (
    (
      <div>
        <h2 className="title gx-mb-4">Transaction Charges</h2>
        <div className="gx-d-flex justify-content-center">
          <Can I="create" a={TRANSACTION_CHARGES}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              data-testid="add-transaction-charge-btn"
              onClick={() => handleShowModal()}
            >
              Add Transaction Charge
            </Button>

          </Can>
          <Input.Search
            data-testid="search-input"
            placeholder="Search user accounts"
            enterButton
            allowClear
            onSearch={handleSearch}
          />
        </div>
        <div className="gx-d-flex justify-content-center">
          <Table data-testid="transaction-charges-table" className="gx-table-responsive" {...props} />
        </div>
        <Modal
          title={editable ? 'Edit Transaction Charge' : 'Add Transaction Charge'}
          open={formVisibility}
          okText={editable ? 'Save' : 'Add'}
          onOk={() => onFormOk(handleOk)}
          confirmLoading={loading}
          onCancel={() => {
            handleCancel();
            handleCancelForm();
          }}
          width={1000}
          destroyOnClose
          centered
        >
          <TransactionChargeForm {...{
            form,
            handleChange,
            selectedPaymentMethod,
            selectedCurrency,
            selectedTransactionType,
            selectedProvider,
            selectedProcessor,
            minimumAmount: Number(selectedMinimumAmount),
            maximumAmount: Number(selectedMaximumAmount),
            selectedTrafficTypes: selectedTrafficTypes || [],
            processors: processorsData,
          }}
          />
        </Modal>
        <InfoView />
      </div>
    )
  );
};

export default TransactionCharges;
